import { MODBUS_EXCEPTIONS, type ModbusResponse } from '../types/modbus';

export class ModbusRTU {
  static calculateCRC(buffer: Uint8Array): number {
    let crc = 0xFFFF;
    for (let i = 0; i < buffer.length; i++) {
      crc ^= buffer[i];
      for (let j = 0; j < 8; j++) {
        if ((crc & 0x0001) !== 0) {
          crc = (crc >> 1) ^ 0xA001;
        } else {
          crc = crc >> 1;
        }
      }
    }
    return crc;
  }

  static bytesToHex(buffer: Uint8Array): string {
    return Array.from(buffer)
      .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');
  }

  static buildReadRequest(
    slaveId: number,
    startRegister: number,
    count: number,
  ): Uint8Array {
    const frame = new Uint8Array(8);
    frame[0] = slaveId;
    frame[1] = 0x03;
    frame[2] = (startRegister >> 8) & 0xff;
    frame[3] = startRegister & 0xff;
    frame[4] = (count >> 8) & 0xff;
    frame[5] = count & 0xff;

    const crc = this.calculateCRC(frame.subarray(0, 6));
    frame[6] = crc & 0xff;
    frame[7] = (crc >> 8) & 0xff;
    return frame;
  }

  static buildWriteRequest(
    slaveId: number,
    register: number,
    value: number,
  ): Uint8Array {
    const frame = new Uint8Array(8);
    frame[0] = slaveId;
    frame[1] = 0x06;
    frame[2] = (register >> 8) & 0xff;
    frame[3] = register & 0xff;
    frame[4] = (value >> 8) & 0xff;
    frame[5] = value & 0xff;

    const crc = this.calculateCRC(frame.subarray(0, 6));
    frame[6] = crc & 0xff;
    frame[7] = (crc >> 8) & 0xff;
    return frame;
  }

  static parseResponse(buffer: Uint8Array): ModbusResponse {
    if (buffer.length < 5) {
      throw new Error('Response too short');
    }

    const slaveId = buffer[0];
    const functionCode = buffer[1];

    if (functionCode & 0x80) {
      const exceptionCode = buffer[2];
      return {
        slaveId,
        functionCode: functionCode & 0x7f,
        values: [],
        exception: exceptionCode,
      };
    }

    const byteCount = buffer[2];
    const values: number[] = [];
    for (let i = 0; i < byteCount; i += 2) {
      const high = buffer[3 + i];
      const low = buffer[4 + i];
      values.push((high << 8) | low);
    }

    return { slaveId, functionCode, values };
  }

  static getExceptionMessage(code: number): string {
    return MODBUS_EXCEPTIONS[code] ?? `Unknown Exception (${code})`;
  }
}
